import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiUrl } from '../utils/api';
import AdminSampleExplorer from '../components/admin/AdminSampleExplorer';
import SamplesCLIInstructions from '../components/admin/SamplesCLIInstructions';

interface SamplesStats {
  total_files: number;
  total_size_bytes: number;
  total_size_human: string;
  bucket: string;
  prefix: string;
}

export default function AdminSampleNotebooksPage() {
  const [stats, setStats] = useState<SamplesStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<string | null>(null);
  const [needsSync, setNeedsSync] = useState(false);
  const navigate = useNavigate();

  const token = localStorage.getItem('authToken');

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchStats();
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchStats = useCallback(async () => {
    if (!token) return;
    setStatsLoading(true);
    try {
      const response = await fetch(`${getApiUrl()}/api/v1/admin/samples/stats`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.status === 403) {
        navigate('/dashboard');
        return;
      }

      if (response.ok) {
        setStats(await response.json());
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    } finally {
      setStatsLoading(false);
    }
  }, [token, navigate]);

  const handleSync = async () => {
    if (!token) return;
    setSyncing(true);
    setSyncResult(null);

    try {
      const response = await fetch(`${getApiUrl()}/api/v1/admin/samples/replicate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        await response.json();
        setSyncResult(`Replication triggered successfully`);
        setNeedsSync(false);
      } else {
        setSyncResult('Replication failed');
      }
    } catch {
      setSyncResult('Replication request failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncNeeded = () => {
    setNeedsSync(true);
    fetchStats();
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">Sample Notebooks</h1>
        <p className="text-sm text-gray-400 mt-1">
          Manage content at <code className="text-gray-300 bg-gray-800 px-1.5 py-0.5 rounded text-xs">/home/propalgos/samples/</code> on user instances
        </p>
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-gray-900 border border-gray-800 rounded-lg px-4 py-3">
          <div className="text-xs text-gray-500 mb-1">Total Files</div>
          <div className="text-lg font-semibold text-white">
            {statsLoading ? (
              <div className="h-5 w-12 bg-gray-800 rounded animate-pulse" />
            ) : (
              stats?.total_files ?? 0
            )}
          </div>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-lg px-4 py-3">
          <div className="text-xs text-gray-500 mb-1">Total Size</div>
          <div className="text-lg font-semibold text-white">
            {statsLoading ? (
              <div className="h-5 w-16 bg-gray-800 rounded animate-pulse" />
            ) : (
              stats?.total_size_human ?? '0 B'
            )}
          </div>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-lg px-4 py-3">
          <div className="text-xs text-gray-500 mb-1">S3 Bucket</div>
          <div className="text-sm font-mono text-gray-300 truncate">
            {stats?.bucket ?? 'propalgos-data-us-east-2'}
          </div>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-lg px-4 py-3">
          <div className="text-xs text-gray-500 mb-1">GCS Replication</div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleSync}
              disabled={syncing}
              className={`flex items-center gap-1.5 px-3 py-1 text-xs rounded transition-colors ${
                needsSync
                  ? 'bg-amber-600 hover:bg-amber-500 text-white'
                  : 'bg-gray-800 hover:bg-gray-700 text-gray-300'
              } disabled:opacity-50`}
            >
              {syncing ? (
                <>
                  <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" opacity="0.25"/>
                    <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  Syncing...
                </>
              ) : (
                <>
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  {needsSync ? 'Sync Needed' : 'Sync to GCP'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Sync result message */}
      {syncResult && (
        <div className={`mb-4 px-4 py-2 rounded-lg text-sm ${
          syncResult.includes('success') ? 'bg-green-900/20 text-green-400 border border-green-800' : 'bg-red-900/20 text-red-400 border border-red-800'
        }`}>
          <div className="flex items-center justify-between">
            <span>{syncResult}</span>
            <button onClick={() => setSyncResult(null)} className="ml-2 opacity-60 hover:opacity-100">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* File explorer */}
      <AdminSampleExplorer onSyncNeeded={handleSyncNeeded} />

      {/* CLI instructions */}
      <div className="mt-6">
        <SamplesCLIInstructions />
      </div>

      {/* Info footer */}
      <div className="mt-6 px-4 py-3 bg-gray-900/50 border border-gray-800 rounded-lg">
        <div className="flex items-start gap-3">
          <svg className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="text-xs text-gray-500 space-y-1">
            <p>AWS instances see changes immediately via FUSE mount (mount-s3). GCP instances require replication to <code className="text-gray-400">propalgos-data-us-central1</code> first.</p>
            <p>Files are mounted read-only at <code className="text-gray-400">/home/propalgos/samples/</code>. Users can copy to my_data/ to edit and persist changes.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
